import React, { useState, useEffect, useRef } from "react";

const NAV_TABS = [
  { id: "home", icon: "🏠", label: "Home" },
  { id: "school", icon: "🏫", label: "School & Activities" },
  { id: "links", icon: "🔗", label: "Quick Links" },
  { id: "essential", icon: "🔒", label: "Essential Info" },
];

const GROUP_COLORS = {
  Potterton: { bg: "#EDE8E0", color: "#7A6A50" },
  Bousfield: { bg: "#EAF4EA", color: "#3A7A3E" },
  Westwood: { bg: "#EEF0FA", color: "#5A6AB0" },
  Gym: { bg: "#FDF3E0", color: "#A07820" },
  Church: { bg: "#FAF0F0", color: "#A05050" },
};

const PINNED_PEOPLE = ["Adam", "Kimberly"];

function getUrgency(person, gifts) {
  const pg = gifts.filter(g => g.recipientIds.includes(person.id));
  if (!pg.length) return null;
  const unbought = pg.filter(g => !g.bought).length;
  if (!unbought) return "done";
  if (!person.eventDate) return "ok";
  const days = Math.ceil((new Date(person.eventDate) - new Date()) / (1000 * 60 * 60 * 24));
  if (days <= 30) return "urgent";
  if (days <= 60) return "soon";
  return "ok";
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
}

function ProgressTrack({ gift, onToggle, readOnly = false }) {
  const steps = ["bought", "shipped", "received", "wrapped"];
  const labels = ["Bought", "Shipped", "Received", "Wrapped"];
  return (
    <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
      {steps.map((step, i) => (
        <div key={step} onClick={readOnly ? undefined : e => { e.stopPropagation(); onToggle(gift.id, step, !gift[step]); }} style={{ flex: 1, cursor: readOnly ? "default" : "pointer" }}>
          <div style={{ width: "100%", height: 8, borderRadius: 4, background: gift[step] ? "var(--sage)" : "var(--border)", transition: "background 0.15s" }} />
          <div style={{ fontSize: 10, color: gift[step] ? "var(--sage)" : "var(--muted)", fontWeight: gift[step] ? 600 : 400, marginTop: 3, textAlign: "center" }}>{labels[i]}</div>
        </div>
      ))}
    </div>
  );
}

function GiftCard({ gift, onToggle, readOnly = false }) {
  return (
    <div style={{ background: "var(--white)", borderRadius: 10, padding: "11px 13px", border: "1px solid var(--border)", marginBottom: 7 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--charcoal)" }}>{gift.name || <span style={{ color: "var(--muted)", fontStyle: "italic" }}>Unnamed gift</span>}</div>
          {gift.note && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{gift.note}</div>}
        </div>
        {gift.stocking && <span style={{ fontSize: 11 }}>🧦</span>}
      </div>
      <ProgressTrack gift={gift} onToggle={onToggle} readOnly={readOnly} />
    </div>
  );
}

function OccasionSection({ occasion, gifts, onToggle, onAddGift, readOnly = false }) {
  const [open, setOpen] = useState(true);
  const bought = gifts.filter(g => g.bought).length;
  return (
    <div style={{ marginBottom: 8 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--charcoal)" }}>{occasion}</span>
          <span style={{ fontSize: 11, color: "var(--muted)" }}>{bought}/{gifts.length} bought</span>
        </div>
        <span style={{ color: "var(--muted)", fontSize: 13, display: "inline-block", transform: open ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}>⌄</span>
      </div>
      {open && (
        <>
          {gifts.map(g => <GiftCard key={g.id} gift={g} onToggle={onToggle} readOnly={readOnly} />)}
          {!readOnly && <button onClick={onAddGift} style={{ width: "100%", padding: "7px 0", borderRadius: 9, border: "1.5px dashed var(--border)", background: "transparent", color: "var(--muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", marginBottom: 4, fontFamily: "inherit" }}>+ Add gift</button>}
        </>
      )}
    </div>
  );
}

function PersonSection({ person, gifts, onToggle, onAddGift, readOnly = false }) {
  const [open, setOpen] = useState(true);
  const urgency = getUrgency(person, gifts);
  const urgencyIcon = urgency === "urgent" ? "🔴" : urgency === "soon" ? "🟡" : urgency === "done" ? "🟢" : "";
  const occasions = [...new Set(gifts.map(g => g.occasion).filter(Boolean))];
  return (
    <div style={{ marginBottom: 4 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "var(--white)", borderRadius: open ? "12px 12px 0 0" : 12, border: "1px solid var(--border)", cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {urgencyIcon && <span style={{ fontSize: 12 }}>{urgencyIcon}</span>}
          <span style={{ fontWeight: 700, fontSize: 15, color: "var(--charcoal)" }}>{person.name}</span>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>{gifts.length} gift{gifts.length !== 1 ? "s" : ""}</span>
        </div>
        <span style={{ color: "var(--muted)", display: "inline-block", transform: open ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}>⌄</span>
      </div>
      {open && (
        <div style={{ padding: "10px 14px 4px", background: "var(--white)", border: "1px solid var(--border)", borderTop: "none", borderRadius: "0 0 12px 12px", marginBottom: 8 }}>
          {occasions.map(occ => (
            <OccasionSection key={occ} occasion={occ} gifts={gifts.filter(g => g.occasion === occ)} onToggle={onToggle} onAddGift={() => onAddGift(person)} readOnly={readOnly} />
          ))}
          {occasions.length === 0 && !readOnly && <button onClick={() => onAddGift(person)} style={{ width: "100%", padding: "7px 0", borderRadius: 9, border: "1.5px dashed var(--border)", background: "transparent", color: "var(--muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>+ Add gift</button>}
        </div>
      )}
    </div>
  );
}

function GroupSection({ groupName, people, gifts, onToggle, onAddGift, readOnly = false }) {
  const [open, setOpen] = useState(true);
  const s = GROUP_COLORS[groupName] || { bg: "var(--cream-hover)", color: "var(--muted)" };
  const groupGifts = gifts.filter(g => people.some(p => g.recipientIds.includes(p.id)));
  const bought = groupGifts.filter(g => g.bought).length;
  return (
    <div style={{ marginBottom: 16 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: open ? 8 : 0, cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: s.bg, color: s.color }}>{groupName}</span>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>{bought}/{groupGifts.length} bought</span>
        </div>
        <span style={{ color: "var(--muted)", fontSize: 13, display: "inline-block", transform: open ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}>⌄</span>
      </div>
      {open && people.map(person => (
        <PersonSection key={person.id} person={person} gifts={gifts.filter(g => g.recipientIds.includes(person.id))} onToggle={onToggle} onAddGift={onAddGift} readOnly={readOnly} />
      ))}
    </div>
  );
}

function NeedsAttention({ people, gifts, personRefs }) {
  const [open, setOpen] = useState(true);
  const urgent = people
    .map(p => ({ person: p, urgency: getUrgency(p, gifts), days: daysUntil(p.eventDate) }))
    .filter(({ urgency }) => urgency === "urgent" || urgency === "soon")
    .sort((a, b) => (a.days || 999) - (b.days || 999));
  if (!urgent.length) return null;
  return (
    <div style={{ marginBottom: 16, background: "var(--white)", borderRadius: 14, border: "1px solid var(--border)", overflow: "hidden" }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", cursor: "pointer" }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: "var(--charcoal)" }}>⚠️ Needs Attention</span>
        <span style={{ color: "var(--muted)", display: "inline-block", transform: open ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}>⌄</span>
      </div>
      {open && (
        <div style={{ borderTop: "1px solid var(--border)" }}>
          {urgent.map(({ person, urgency, days }) => {
            const icon = urgency === "urgent" ? "🔴" : "🟡";
            const unbought = gifts.filter(g => g.recipientIds.includes(person.id) && !g.bought).length;
            return (
              <div key={person.id} onClick={() => personRefs.current[person.id]?.scrollIntoView({ behavior: "smooth", block: "start" })} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span>{icon}</span>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 13, color: "var(--charcoal)" }}>{person.name}</span>
                    <span style={{ fontSize: 12, color: "var(--muted)", marginLeft: 6 }}>{unbought} not bought{days !== null ? ` · ${days}d away` : ""}</span>
                  </div>
                </div>
                <span style={{ fontSize: 12, color: "var(--sage)", fontWeight: 600 }}>↓</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LinksSection() {
  const [ideasOpen, setIdeasOpen] = useState(false);
  const [wishOpen, setWishOpen] = useState(false);
  const IDEA_LINKS = [
    { label: "Pinterest Gift Ideas", url: "https://www.pinterest.com/carissalynp/gift-ideas/" },
    { label: "Amazon Gift Ideas List", url: "https://www.amazon.com/hz/wishlist/ls/3UKOVYINYKNTK" },
  ];
  const WISH_LINKS = [
    { label: "My Pinterest Wishlist", url: "https://www.pinterest.com/carissalynp/things-i-want/" },
    { label: "My Amazon List 1", url: "https://www.amazon.com/hz/wishlist/ls/2BOSBDB7W94A9?ref_=wl_share" },
    { label: "My Amazon List 2", url: "https://www.amazon.com/hz/wishlist/ls/39P9O2UJM261R?ref_=wl_share" },
    { label: "My Amazon List 3", url: "https://www.amazon.com/hz/wishlist/ls/1WE8J94TQGGRN?ref_=wl_share" },
  ];
  const linkRow = (label, url) => (
    <a key={url} href={url} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 10, background: "var(--cream-hover)", textDecoration: "none", fontSize: 14, fontWeight: 600, color: "var(--charcoal)", marginBottom: 6 }}>
      {label}<span style={{ fontSize: 14, color: "var(--muted)" }}>↗</span>
    </a>
  );
  return (
    <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ background: "var(--white)", borderRadius: 12, border: "1px solid var(--border)", overflow: "hidden" }}>
        <div onClick={() => setIdeasOpen(o => !o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", cursor: "pointer" }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: "var(--charcoal)" }}>💡 Gift Ideas</span>
          <span style={{ color: "var(--muted)", display: "inline-block", transform: ideasOpen ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}>⌄</span>
        </div>
        {ideasOpen && <div style={{ borderTop: "1px solid var(--border)", padding: "10px 12px" }}>{IDEA_LINKS.map(l => linkRow(l.label, l.url))}</div>}
      </div>
      <div style={{ background: "var(--white)", borderRadius: 12, border: "1px solid var(--border)", overflow: "hidden" }}>
        <div onClick={() => setWishOpen(o => !o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", cursor: "pointer" }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: "var(--charcoal)" }}>🎁 My Wishlists</span>
          <span style={{ color: "var(--muted)", display: "inline-block", transform: wishOpen ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}>⌄</span>
        </div>
        {wishOpen && <div style={{ borderTop: "1px solid var(--border)", padding: "10px 12px" }}>{WISH_LINKS.map(l => linkRow(l.label, l.url))}</div>}
      </div>
      <a href="https://docs.google.com/document/d/14OGO47c_yOI5mTSyWfbLCPkK5JakbXfTIr2aD_ttwyQ/edit?usp=drivesdk" target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderRadius: 12, background: "var(--white)", border: "1px solid var(--border)", textDecoration: "none" }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: "var(--charcoal)" }}>📋 Potterton Wishlists</span>
        <span style={{ fontSize: 13, color: "var(--muted)", fontWeight: 600 }}>Open in Drive ↗</span>
      </a>
    </div>
  );
}

function ClaudeSection({ gifts, people, occasions }) {
  const [input, setInput] = useState("");
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  const PROMPTS = [
    "Who still needs gifts?",
    "What's not wrapped yet?",
    `What's left for ${occasions[0] || "this year"}?`,
    "Suggest gift ideas for Kimberly",
    "What's my overall gift progress?",
  ];

  const buildContext = () => {
    const peopleMap = Object.fromEntries(people.map(p => [p.id, p.name]));
    const summary = gifts.map(g => ({
      name: g.name || "(unnamed)",
      recipient: g.recipientIds.map(id => peopleMap[id] || "Unknown").join(", "),
      occasion: g.occasion,
      bought: g.bought, wrapped: g.wrapped, shipped: g.shipped, received: g.received,
    }));
    return JSON.stringify(summary);
  };

  const runPrompt = async (prompt) => {
    setLoading(true); setResponse(null);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 1000,
          system: `You are a helpful assistant for the Potterton family gift tracking app. Answer questions about their gift data concisely. Here is their current gift data: ${buildContext()}`,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const data = await res.json();
      const text = data.content?.find(b => b.type === "text")?.text || "No response.";
      setResponse(text);
    } catch { setResponse("Something went wrong. Try again."); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ marginTop: 24, background: "var(--white)", borderRadius: 14, border: "1px solid var(--border)", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "var(--charcoal)", marginBottom: 2 }}>✨ Ask Claude</div>
        <div style={{ fontSize: 12, color: "var(--muted)" }}>Ask questions or take actions on your gift data</div>
      </div>
      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8, borderBottom: "1px solid var(--border)" }}>
        {PROMPTS.map(p => (
          <button key={p} onClick={() => runPrompt(p)} style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--cream-hover)", color: "var(--charcoal)", fontSize: 13, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>{p}</button>
        ))}
      </div>
      <div style={{ padding: "12px 16px", display: "flex", gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && input && runPrompt(input)}
          placeholder="Ask anything about your gifts…"
          style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1.5px solid var(--border)", fontSize: 13, background: "var(--cream-hover)", outline: "none", fontFamily: "inherit" }} />
        <button onClick={() => input && runPrompt(input)} style={{ padding: "10px 14px", borderRadius: 10, background: "var(--sage)", border: "none", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>↑</button>
      </div>
      {loading && <div style={{ padding: "0 16px 16px", fontSize: 13, color: "var(--muted)", fontStyle: "italic" }}>Checking your gift data…</div>}
      {response && !loading && (
        <div style={{ margin: "0 16px 16px", padding: "12px 14px", background: "#F0F5F0", borderRadius: 10, fontSize: 13, color: "var(--charcoal)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{response}</div>
      )}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  return (
    <button onClick={handleCopy} style={{
      padding: "7px 14px", borderRadius: 9, border: "none", cursor: "pointer",
      background: copied ? "var(--sage)" : "var(--cream-hover)",
      color: copied ? "#fff" : "var(--charcoal)",
      fontWeight: 700, fontSize: 12, whiteSpace: "nowrap", flexShrink: 0, fontFamily: "inherit",
      transition: "all 0.15s",
    }}>
      {copied ? "Copied ✓" : "Copy"}
    </button>
  );
}

function YearlyResetSection({ years, occasions }) {
  const [open, setOpen] = useState(false);
  const currentYear = years.length ? Math.max(...years.map(y => parseInt(y, 10))) : new Date().getFullYear();
  const nextYear = currentYear + 1;
  const currentOccasions = occasions.filter(o => o.includes(String(currentYear)));

  const STEPS = [
    {
      id: 1,
      label: `Archive all ${currentYear} gifts`,
      description: `Sets Archive = true on every gift where Year is ${currentYear}.`,
      prompt: `Archive all gifts in the Gift Tracker database where Year is "${currentYear}" by setting Archive = true on each one. Don't touch any gifts from other years.`,
    },
    {
      id: 2,
      label: `Add ${nextYear} occasion options`,
      description: currentOccasions.length
        ? `Adds these as ${nextYear} versions: ${currentOccasions.map(o => o.replace(String(currentYear), String(nextYear))).join(", ")}.`
        : `Adds a ${nextYear} version of each ${currentYear} occasion option.`,
      prompt: `In the Gift Tracker database, look at the current Occasion field options. For every option containing "${currentYear}", add a new matching option with "${nextYear}" instead (e.g. "Christmas ${currentYear}" becomes a new option "Christmas ${nextYear}"). Don't remove the old ${currentYear} options.`,
    },
    {
      id: 3,
      label: "Roll forward event dates",
      description: "Adds one year to every person's Gift Event Date in the People database.",
      prompt: `In the People database, for every person, take their existing Gift Event Date and update it by adding exactly one year (e.g. Dec 25, ${currentYear} becomes Dec 25, ${nextYear}). Keep the month and day the same, only change the year.`,
    },
    {
      id: 4,
      label: "Create placeholder gifts",
      description: `For every person with at least one ${currentYear} gift record, creates one blank ${nextYear} gift per occasion they had.`,
      prompt: `In the Gift Tracker database, find every person who has at least one gift record with Year = "${currentYear}" (regardless of whether it was bought). For each of those people, look at the distinct Occasions they had gifts for in ${currentYear}. For each person-occasion combination, create one new blank gift record with: Gift title left blank, Recipient set to that person, Occasion set to the ${nextYear} version of that occasion (e.g. "Christmas ${currentYear}" → "Christmas ${nextYear}"), Year set to "${nextYear}", and all checkboxes (Bought, Wrapped, Shipped, Received, Stocking, Archive) left unchecked.`,
    },
  ];

  return (
    <div style={{ marginTop: 16, background: "var(--white)", borderRadius: 14, border: "1.5px solid var(--border)", overflow: "hidden" }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", cursor: "pointer" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--charcoal)" }}>🔄 Yearly Reset</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>Copy each prompt and run it here or in a Claude chat, in order</div>
        </div>
        <span style={{ color: "var(--muted)", display: "inline-block", transform: open ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}>⌄</span>
      </div>
      {open && (
        <div style={{ borderTop: "1px solid var(--border)" }}>
          {STEPS.map((step, i) => (
            <div key={step.id} style={{ padding: "14px 16px", borderBottom: i < STEPS.length - 1 ? "1px solid var(--border)" : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, background: "var(--cream-hover)", color: "var(--muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>{i + 1}</div>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "var(--charcoal)" }}>{step.label}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--muted)", paddingLeft: 30, lineHeight: 1.5 }}>{step.description}</div>
                </div>
                <CopyButton text={step.prompt} />
              </div>
              <div style={{ marginLeft: 30, padding: "10px 12px", background: "var(--cream-hover)", borderRadius: 9, fontSize: 12, color: "var(--mid)", lineHeight: 1.5, fontStyle: "italic" }}>
                {step.prompt}
              </div>
            </div>
          ))}
          <div style={{ padding: "12px 16px", background: "#FFF8F0", borderTop: "1px solid var(--border)" }}>
            <div style={{ fontSize: 12, color: "var(--amber)", fontWeight: 600 }}>⚠️ Run in order. Each one writes real changes to Notion, so review before sending.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function NoGiftsSection({ people, onAddGift, readOnly = false }) {
  const [open, setOpen] = useState(false);
  if (!people.length || readOnly) return null;
  return (
    <div style={{ marginTop: 16, background: "var(--white)", borderRadius: 14, border: "1px solid var(--border)", overflow: "hidden" }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", cursor: "pointer" }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: "var(--muted)" }}>No gifts yet ({people.length})</span>
        <span style={{ color: "var(--muted)", display: "inline-block", transform: open ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}>⌄</span>
      </div>
      {open && (
        <div style={{ borderTop: "1px solid var(--border)" }}>
          {people.map(p => (
            <div key={p.id} onClick={() => onAddGift(p)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--charcoal)" }}>{p.name}</span>
              <span style={{ fontSize: 12, color: "var(--sage)", fontWeight: 700 }}>+ Add gift</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddGiftSheet({ person, occasions, onClose, onAdd }) {
  const [name, setName] = useState("");
  const [occasion, setOccasion] = useState(occasions[0] || "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!name.trim() && !occasion) return;
    setSaving(true);
    try {
      const res = await fetch("/.netlify/functions/notion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "createGift", value: { name: name.trim(), recipientId: person?.id, occasion, note, year: new Date().getFullYear().toString() } }),
      });
      const data = await res.json();
      if (data.success) { onAdd({ id: data.id, name: name.trim(), occasion, note, recipientIds: person ? [person.id] : [], bought: false, wrapped: false, shipped: false, received: false, stocking: false }); }
    } catch {}
    finally { setSaving(false); onClose(); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} />
      <div style={{ position: "relative", background: "var(--white)", borderRadius: "20px 20px 0 0", padding: "20px 20px 40px", boxShadow: "0 -4px 24px rgba(0,0,0,0.12)" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} /></div>
        <div style={{ fontWeight: 800, fontSize: 18, color: "var(--charcoal)", marginBottom: 16 }}>Add Gift{person ? ` for ${person.name}` : ""}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Gift name" style={{ padding: "12px 14px", borderRadius: 10, border: "1.5px solid var(--border)", fontSize: 14, background: "var(--cream-hover)", outline: "none", fontFamily: "inherit" }} />
          <select value={occasion} onChange={e => setOccasion(e.target.value)} style={{ padding: "12px 14px", borderRadius: 10, border: "1.5px solid var(--border)", fontSize: 14, background: "var(--cream-hover)", fontFamily: "inherit" }}>
            {occasions.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)" style={{ padding: "12px 14px", borderRadius: 10, border: "1.5px solid var(--border)", fontSize: 14, background: "var(--cream-hover)", outline: "none", fontFamily: "inherit" }} />
          <button onClick={handleAdd} disabled={saving} style={{ padding: "13px 0", borderRadius: 10, background: "var(--sage)", border: "none", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>{saving ? "Adding…" : "Add Gift"}</button>
        </div>
      </div>
    </div>
  );
}

export default function GiftsPage({ onBack, onTabTap, onKidsGiftsTap, readOnly = false }) {
  const [occasion, setOccasion] = useState(() => { try { return localStorage.getItem("gifts-occasion") || ""; } catch { return ""; } });
  const [year, setYear] = useState(() => { try { return localStorage.getItem("gifts-year") || ""; } catch { return ""; } });
  const [occasions, setOccasions] = useState([]);
  const [years, setYears] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [allGifts, setAllGifts] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingFor, setAddingFor] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const personRefs = useRef({});

  useEffect(() => {
    fetch("/.netlify/functions/notion?type=giftOccasions")
      .then(r => r.json()).then(d => { setOccasions(d.options || []); setYears(d.years || []); }).catch(() => {});
    fetch("/.netlify/functions/notion?type=giftPeople")
      .then(r => r.json()).then(d => setPeople(d.items || [])).catch(() => {});
    // Unfiltered fetch (no occasion/year) used only to determine which people
    // have zero gifts at all, independent of the occasion/year filter above.
    fetch("/.netlify/functions/notion?type=gifts")
      .then(r => r.json()).then(d => setAllGifts(d.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    try { localStorage.setItem("gifts-occasion", occasion); localStorage.setItem("gifts-year", year); } catch {}
    setLoading(true); setError(null);
    const params = new URLSearchParams({ type: "gifts" });
    if (occasion) params.set("occasion", occasion);
    if (year) params.set("year", year);
    fetch(`/.netlify/functions/notion?${params}`)
      .then(r => r.json())
      .then(d => { if (d.error) { setError(JSON.stringify(d.error)); setGifts([]); } else setGifts(d.items || []); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [occasion, year]);

  const handleToggle = async (giftId, field, val) => {
    setGifts(prev => prev.map(g => g.id === giftId ? { ...g, [field]: val } : g));
    await fetch("/.netlify/functions/notion", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pageId: giftId, action: "updateGiftProgress", value: { [field]: val } }),
    });
  };

  const handleAddGift = (person) => { setAddingFor(person); setShowAdd(true); };
  const handleGiftAdded = (gift) => {
    setGifts(prev => [gift, ...prev]);
    setAllGifts(prev => [gift, ...prev]);
    setShowAdd(false);
  };

  // People with zero gifts at all (across every occasion/year), based on the
  // unfiltered allGifts fetch — kept separate from the occasion/year filter
  // so this bucket doesn't shift as the dropdowns change.
  const peopleWithAnyGifts = new Set(allGifts.flatMap(g => g.recipientIds));
  const zeroGiftPeople = people.filter(p => !peopleWithAnyGifts.has(p.id));

  const pinnedPeople = people.filter(p => PINNED_PEOPLE.includes(p.name) && peopleWithAnyGifts.has(p.id));
  const groups = [...new Set(people.filter(p => !PINNED_PEOPLE.includes(p.name) && peopleWithAnyGifts.has(p.id)).map(p => p.group).filter(Boolean))];

  return (
    <>
      <div className="app">
        <header style={{ background: "var(--white)", padding: "54px 20px 14px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>‹</button>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 600, color: "var(--charcoal)" }}>🎁 Gifts</span>
          </div>
          {!readOnly && <button onClick={() => { setAddingFor(null); setShowAdd(true); }} style={{ padding: "8px 16px", borderRadius: 10, background: "var(--sage)", border: "none", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>+ Add</button>}
        </header>

        <div className="scroll-body" style={{ padding: "12px 16px", paddingBottom: 80 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <select value={occasion} onChange={e => setOccasion(e.target.value)} style={{ flex: 1, padding: "9px 12px", borderRadius: 10, border: `1.5px solid ${occasion ? "var(--charcoal)" : "var(--border)"}`, background: occasion ? "var(--charcoal)" : "var(--white)", color: occasion ? "#fff" : "var(--charcoal)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              <option value="">All Occasions</option>
              {occasions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <select value={year} onChange={e => setYear(e.target.value)} style={{ padding: "9px 12px", borderRadius: 10, border: `1.5px solid ${year ? "var(--charcoal)" : "var(--border)"}`, background: year ? "var(--charcoal)" : "var(--white)", color: year ? "#fff" : "var(--charcoal)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              <option value="">All Years</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {!readOnly && (
            <div onClick={onKidsGiftsTap} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 12, cursor: "pointer", marginBottom: 16 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "var(--charcoal)" }}>🔒 Kids' Gifts</span>
              <span style={{ color: "var(--muted)", fontSize: 16 }}>›</span>
            </div>
          )}

          <LinksSection />

          {error && <div style={{ padding: 12, background: "#FFF0F0", borderRadius: 10, fontSize: 13, color: "var(--rose)", marginBottom: 16 }}>Error loading gifts: {error}</div>}

          {loading ? <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Loading…</div> : (
            <>
              <NeedsAttention people={people} gifts={gifts} personRefs={personRefs} />
              {pinnedPeople.map(p => (
                <div key={p.id} ref={el => personRefs.current[p.id] = el} style={{ marginBottom: 8 }}>
                  <PersonSection person={p} gifts={gifts.filter(g => g.recipientIds.includes(p.id))} onToggle={handleToggle} onAddGift={handleAddGift} readOnly={readOnly} />
                </div>
              ))}
              {groups.map(group => {
                const groupPeople = people.filter(p => p.group === group && !PINNED_PEOPLE.includes(p.name) && peopleWithAnyGifts.has(p.id));
                const groupGifts = gifts.filter(g => groupPeople.some(p => g.recipientIds.includes(p.id)));
                if (!groupGifts.length && !groupPeople.length) return null;
                return (
                  <div key={group}>
                    {groupPeople.map(p => <div key={p.id} ref={el => personRefs.current[p.id] = el} />)}
                    <GroupSection groupName={group} people={groupPeople} gifts={gifts} onToggle={handleToggle} onAddGift={handleAddGift} readOnly={readOnly} />
                  </div>
                );
              })}
              <NoGiftsSection people={zeroGiftPeople} onAddGift={handleAddGift} readOnly={readOnly} />
              <ClaudeSection gifts={gifts} people={people} occasions={occasions} />
              <YearlyResetSection years={years} occasions={occasions} />
            </>
          )}
        </div>

        <nav style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 }}>
          {NAV_TABS.map(t => (
            <div key={t.id} onClick={() => onTabTap?.(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" }}>
              <div style={{ fontSize: 22 }}>{t.icon}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
            </div>
          ))}
        </nav>
      </div>
      {showAdd && <AddGiftSheet person={addingFor} occasions={occasions} onClose={() => setShowAdd(false)} onAdd={handleGiftAdded} />}
    </>
  );
}
